import { createAsyncThunk } from '@reduxjs/toolkit';

import APIDataProvider, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api/ApiDataProvider';
import { ApiPaths } from '../../../api/APIPaths';
import { ICustomQuiz } from '.';
import {
  ISection,
  TCustomQuizCriteria,
  TCustomQuizStatus,
} from '../SectionSlice';

export interface TCreateCustomQuiz {
  title: string;
  questions: string[];
  isPublished?: boolean;
}

export type TGetCustomQuizList = {
  limit: number;
  offset: number;
  sortKey?: string;
  sortValue?: number;
  searchText?: string;
};

export interface TUpdateCustomQuiz {
  id: string;
  title?: string;
  isPublished?: boolean;
  questions?: string[];
}

export interface TAssignCustomQuiz {
  customQuiz: string;
  course: string;
  section?: string;
  sectionName?: string;
  dueDate: string;
  customQuizCriteria?: TCustomQuizCriteria;
  students?: string[];
}

export type TGetAssignList = {
  customQuiz: string;
  limit: number;
  offset: number;
  sortKey?: string;
  sortValue?: number;
  searchText?: string;
};

export type TGetAssignListForStudent = {
  limit: number;
  offset: number;
  sortKey?: string;
  sortValue?: number;
  searchText?: string;
  status?: TCustomQuizStatus;
};

const apiDataProvider = new APIDataProvider();

export const createCustomQuiz = createAsyncThunk(
  '/custom-quiz/create',
  async (data: TCreateCustomQuiz) => {
    const path = `${ApiPaths.customQuiz}`;
    const response = await apiDataProvider.post<TApiResponse<ICustomQuiz>>(
      path,
      data
    );

    return response.data;
  }
);

export const getCustomQuizById = createAsyncThunk(
  '/custom-quiz/getOne',
  async (data: { id: string }) => {
    const path = `${ApiPaths.customQuiz}/${data.id}`;
    const response = await apiDataProvider.get<TApiResponse<ICustomQuiz>>(path);

    return response.data;
  }
);

export const getCustomQuizList = createAsyncThunk(
  '/custom-quiz/list',
  async ({
    limit,
    offset,
    sortKey,
    sortValue,
    searchText,
  }: TGetCustomQuizList) => {
    let path = `${ApiPaths.customQuiz}?limit=${limit}&offset=${offset}`;

    if (searchText) {
      path += `&searchText=${searchText}`;
    }
    if (sortKey) {
      path += `&sortKey=${sortKey}`;
    }
    if (sortValue) {
      path += `&sortValue=${sortValue}`;
    }

    const response = await apiDataProvider.get<
      TApiResponse<TApiPaginationResponse<ICustomQuiz>>
    >(path);

    return response.data;
  }
);

export const getCustomQuizListForDropDown = createAsyncThunk(
  '/custom-quiz/dropdown-list',
  async () => {
    const path = `${ApiPaths.customQuiz}/dropdown`;
    const response = await apiDataProvider.get<TApiResponse<ICustomQuiz[]>>(
      path
    );
    return response.data;
  }
);

export const updateCustomQuiz = createAsyncThunk(
  '/custom-quiz/update',
  async ({ id, ...data }: TUpdateCustomQuiz) => {
    const path = `${ApiPaths.customQuiz}/${id}`;
    const response = await apiDataProvider.put<TApiResponse<ICustomQuiz>>(
      path,
      data
    );

    return response.data;
  }
);

export const assignCustomQuiz = createAsyncThunk(
  '/custom-quiz/assign',
  async (data: TAssignCustomQuiz) => {
    const path = `${ApiPaths.customQuiz}/assign`;
    const response = await apiDataProvider.post<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const getAssignList = createAsyncThunk(
  '/custom-quiz/assign-list',
  async ({
    customQuiz,
    limit,
    offset,
    sortKey,
    sortValue,
    searchText,
  }: TGetAssignList) => {
    let path = `${ApiPaths.customQuiz}/assign?customQuiz=${customQuiz}&limit=${limit}&offset=${offset}`;

    if (searchText) {
      path += `&searchText=${searchText}`;
    }
    if (sortKey) {
      path += `&sortKey=${sortKey}`;
    }
    if (sortValue) {
      path += `&sortValue=${sortValue}`;
    }

    const response = await apiDataProvider.get<
      TApiResponse<TApiPaginationResponse<ISection>>
    >(path);

    return response.data;
  }
);

export const getAssignListForStudent = createAsyncThunk(
  '/custom-quiz/assign-list-for-student',
  async ({
    limit,
    offset,
    sortKey,
    sortValue,
    searchText,
    status,
  }: TGetAssignListForStudent) => {
    let path = `${ApiPaths.customQuiz}/student/assign?limit=${limit}&offset=${offset}`;

    if (searchText) {
      path += `&searchText=${searchText}`;
    }
    if (sortKey) {
      path += `&sortKey=${sortKey}`;
    }
    if (sortValue) {
      path += `&sortValue=${sortValue}`;
    }
    if (status) {
      path += `&status=${status}`;
    }

    const response = await apiDataProvider.get<
      TApiResponse<TApiPaginationResponse<ISection>>
    >(path);

    return response.data;
  }
);

export const deleteCustomQuiz = createAsyncThunk(
  '/custom-quiz/delete',
  async (data: { id: string }) => {
    const path = `${ApiPaths.customQuiz}/${data.id}`;
    const response = await apiDataProvider.delete<TApiResponse<ICustomQuiz>>(
      path
    );

    return response.data;
  }
);
